/**
 * ORA - AI Leadership Agent
 * Knowledge Base Search Utility
 * Copyright (c) 2024-2026 s2cpaul
 * Licensed under MIT License
 */

/**
 * Search the knowledge base for relevant information based on user query
 * @param query - The user's question
 * @returns A relevant response from the knowledge base
 */
export function searchKnowledgeBase(query: string): string {
  const lowerQuery = query.toLowerCase();
  
  // AI Literacy keywords
  if (lowerQuery.includes("ai literacy") || lowerQuery.includes("what is ai") || lowerQuery.includes("artificial intelligence")) {
    return `Great question about AI literacy! Artificial Intelligence (AI) literacy is the ability to understand, use, and evaluate AI tools responsibly through trusted, evidence‑based frameworks. It involves knowing how AI works, what it can and cannot do, and how to use it effectively and ethically.

As Forbes notes: "Career success increasingly depends on the ability to collaborate with AI systems, not just traditional skills."

The World Economic Forum predicts 170 million new roles will be created by AI over the next decade, making AI literacy essential for workforce readiness.

AI literacy enables the workforce to recognize bias and risk before they cause costly losses. Would you like to learn more about AI frameworks or governance?`;
  }
  
  // RACI Matrix keywords
  if (lowerQuery.includes("raci") || lowerQuery.includes("responsibility") || lowerQuery.includes("accountability")) {
    return `Excellent question about RACI! A RACI Matrix (also called a Responsibility Assignment Matrix) is a project-management tool used to define who does what for every task or deliverable.

RACI stands for:
• R - Responsible: The person who does the work to complete the task
• A - Accountable: The person who owns the task and makes final decisions (only one per task)
• C - Consulted: People who provide input, expertise, or feedback
• I - Informed: People who must be kept updated on progress

A RACI matrix helps clarify roles, strengthens governance, supports compliance, and improves operational efficiency. It's a fundamental tool in AI governance and digital transformation!`;
  }
  
  // Frameworks keywords
  if (lowerQuery.includes("framework") || lowerQuery.includes("nist") || lowerQuery.includes("agile") || lowerQuery.includes("scrum")) {
    return `Great question about frameworks! AI enabled leaders must be well versed in an array of proven frameworks. Frameworks are trusted guardrails for success when working within complex systems.

Frameworks like NIST's AI Risk Management Framework, Agile, Scrum, and ORA provide structured ways to balance innovation with governance. Here's why they matter:

✅ Build Momentum: They offer clear phases (Crawl > Walk > Run) that are familiar and human-centered
✅ Align Teams: They give everyone a shared language so smarter decisions move quicker
✅ Unlock Speed at Scale: They turn messy AI work into clear, repeatable steps so teams can move faster without reinventing the process

Would you like to explore a specific framework in more detail?`;
  }
  
  // KPI and ROI keywords
  if (lowerQuery.includes("kpi") || lowerQuery.includes("roi") || lowerQuery.includes("return on investment") || lowerQuery.includes("metrics")) {
    return `Excellent question about AI governance KPIs! An AI Governance KPI Dashboard strengthens transparency and maximizes return on investment by giving leaders a clear view of model reliability, data‑drift incidents, fairness audit coverage, and overall operational performance.

Key KPIs include:

📊 Governance Transparency & Accessibility Score (TAS)
Tracks the percentage of AI governance materials that are published, up‑to‑date, and accessible. Target: 95% publication in a single, searchable repository.

🔗 Connected Automated Processes (CAP) Count
Measures how many business processes are fully integrated and automated end‑to‑end with current SOPs. Shows progress toward enterprise‑wide automation.

The added value? It helps monitor and increase ROI, provides transparency, builds trust, and creates opportunities for human oversight!`;
  }
  
  // Clinger-Cohen Act keywords
  if (lowerQuery.includes("clinger") || lowerQuery.includes("cohen") || lowerQuery.includes("it investment") || lowerQuery.includes("federal")) {
    return `Great question! The Clinger‑Cohen Act was enacted in 1996, combining the Information Technology Management Reform Act and the Federal Acquisition Reform Act.

Under the Clinger‑Cohen Act, AI spending must be:
✅ Justified
✅ Measurable
✅ Mission‑aligned
✅ Managed as a strategic IT investment—not a tech experiment

This ensures responsible stewardship of resources and maximizes return on investment. It's not just an AI checkmark — it's financial stewardship and responsible citizenship!

This is particularly important for government agencies and contractors working with federal systems.`;
  }
  
  // Open Government keywords
  if (lowerQuery.includes("open government") || lowerQuery.includes("transparency") || lowerQuery.includes("foia")) {
    return `Excellent question about transparency! Open government is built on transparency, participation, and collaboration, as described in the Open Government Directive issued by the Office of Management and Budget in 2009 (M-10-06).

Key principles:
🔓 Transparency in government operations is a priority (Attorney General's FOIA Guidelines, March 2022)
🤝 The Open Government Act and FOIA promote public access to information and accountable decision‑making
📋 Digital transformation requires transparent AI governance policies

Transparency and collaboration are consistent with responsible AI implementation and financial stewardship!`;
  }
  
  // AI Risk and Bias keywords
  if (lowerQuery.includes("bias") || lowerQuery.includes("risk") || lowerQuery.includes("vendor lock")) {
    return `Critical question about AI risk! AI literacy enables the workforce to recognize bias and risk before they cause costly losses.

⚠️ AI Bias:
Algorithms may weigh certain features (like word choice, school, or zip code) that highly correlate with race, gender, or socioeconomic status.

🔒 AI Risk - Vendor Lock-in:
Vendor lock‑in is both a security threat and an innovation blocker. It creates systemic vulnerabilities, reduces visibility, and makes it harder to switch to more secure alternatives. Restrictive contracts and prohibitive proprietary integrations prevent experimentation and organizational growth at scale.

Responsible AI governance helps identify and mitigate these risks before they become costly problems!`;
  }
  
  // Tokens and AI Costs keywords
  if (lowerQuery.includes("token") || lowerQuery.includes("cost") || lowerQuery.includes("pricing") || lowerQuery.includes("expensive")) {
    return `Great question about AI costs! Everything has a cost, and AI is no exception. Every piece of information an AI processes must be converted into tokens, and that conversion takes real energy, compute, and infrastructure.

Here's how it works:
1️⃣ Information → Data: AI models convert information into tokens — small units of data
2️⃣ Tokens: Words, phrases, or chunks of text become the basic units AI processes
3️⃣ Cost: Every token processed has a cost. More tokens = higher expense
4️⃣ Limits: Most APIs use a "Pay-As-You-Go" structure

As AI usage grows, the energy required to generate and process these tokens increases, driving higher electricity demand and expanding the environmental footprint of data centers worldwide.

To get real ROI, we must understand these costs and manage them responsibly!`;
  }
  
  // Agentic AI keywords
  if (lowerQuery.includes("agentic") || lowerQuery.includes("agent") || lowerQuery.includes("multi-agent")) {
    return `Excellent question about Agentic AI! Agentic AI may look like a simple bot, but it's actually the front door to an entire connected ecosystem.

The true power is unlocked by Agentic AI: building agents that can spot patterns, reason, coordinate, and act across your ecosystem.

**Agentic AI can drive change and efficiency by connecting automated tasks with expert context:**
• By autonomously orchestrating multistage workflows, agentic AI brings machine speed to complicated processes.
• It offers the huge scaling advantages of AI to workflows that have historically been constrained by the limitations of human workforces.
• Agentic AI brings agility through the ability to dynamically adjust strategy or approach when operational conditions change.
• And thanks to its interoperability with diverse tools and platforms (e.g., through application programming interfaces and Model Context Protocol), agentic AI can add value without requiring a wholesale refresh of your IT portfolio.

Key points:
🤖 AI isn't a model or a tool — it's a whole stack
🔗 Transformation happens when those layers connect, with humans at the center
👥 Effective oversight never rests on a single person
✅ Agents provide the judgment, context, and accountability that AI alone can't deliver

Customer experience stays trustworthy and adaptive when human‑in‑the‑loop oversight shapes every AI‑driven interaction — ensuring every outcome aligns with expectations and organizational values!`;
  }
  
  // NotebookLM keywords
  if (lowerQuery.includes("notebooklm") || lowerQuery.includes("research") || lowerQuery.includes("note")) {
    return `Great question about research tools! NotebookLM is the AI‑powered research and note‑taking tool from Google.

It lets you upload your own materials—PDFs, Google Docs, websites, YouTube videos, audio files, and more—and then it becomes a kind of personalized expert on those sources.

AI leaders can research and articulate new capabilities with learning and study support. The most valuable skill isn't finding information; it's filtering it. Being able to verify sources, detect bias, and combine insights is what separates passive consumers from future analysts, founders, and journalists.

Visit https://notebooklm.google.com to get started!`;
  }
  
  // MVG (Minimum Viable Governance) keywords
  if (lowerQuery.includes("mvg") || lowerQuery.includes("minimum viable") || lowerQuery.includes("governance")) {
    return `Excellent question about governance! If you want to move fast, you need Minimum Viable Governance (MVG) — just enough structure to stay safe without slowing innovation.

MVG works because AI evolves faster than any policy manual ever will. Build guardrails, not roadblocks — and make sure your AI Governance Key Performance Indicators are transparent, explicit, and measurable.

Key principles:
✅ Transparent policies and KPIs
✅ RBAC (Role-Based Access Controls) - NIST standard
✅ Clear incident response processes
✅ Regular audits and feedback mechanisms

This ensures responsible AI adoption while maintaining the speed necessary for innovation!`;
  }
  
  // Micro lessons keywords
  if (lowerQuery.includes("micro") || lowerQuery.includes("lesson") || lowerQuery.includes("training")) {
    return `Micro Lessons are built for busy people on the move — short, focused Artificial Intelligence (AI) learning experiences that help you build practical skills fast. Effective AI leadership demands agility and a readiness to learn, and these lessons support exactly that!

Key benefits:
📱 Mobile-friendly format
⚡ Quick, focused learning
🎯 Practical AI skills
🔊 Audio playback available

This approach makes AI literacy accessible for everyone, regardless of schedule constraints!`;
  }
  
  // Digital Personas keywords
  if (lowerQuery.includes("persona") || lowerQuery.includes("user experience") || lowerQuery.includes("ux")) {
    return `Leveraging personas can accelerate development, boost customer satisfaction, and increase overall return on investment. Digital personas help teams make clear, focused decisions that stay aligned with the people they support.

They turn raw data into relatable profiles that reflect real values, behaviors, needs, and motivations, making digital transformation easier to understand and execute. In fast‑moving environments, digital personas drive innovation and speed up development, especially in human‑centered design.`;
  }
  
  // Additive Manufacturing keywords
  if (lowerQuery.includes("additive") || lowerQuery.includes("3d printing") || lowerQuery.includes("3d print") || lowerQuery.includes("manufacturing")) {
    return `Excellent question about Additive Manufacturing! Additive manufacturing (AM) is a technology that creates objects by building them layer‑by‑layer from digital 3D models, instead of cutting material away like traditional machining. It's often called 3D printing, and it enables complex shapes, rapid prototyping, and customized production.

🧩 **What Additive Manufacturing Actually Does:**
• Uses a digital 3D model as the blueprint
• Builds the object one thin layer at a time using materials such as plastics, metals, ceramics, or composites
• Produces shapes that are difficult or impossible to make with traditional manufacturing

🛠️ **Common Additive Manufacturing Processes:**
• Fused Deposition Modeling (FDM): melts plastic filament and deposits it layer by layer
• Stereolithography (SLA): uses UV light to cure liquid resin
• Selective Laser Sintering (SLS): fuses powdered materials with a laser
• Direct Metal Laser Sintering (DMLS) / SLM: creates metal parts using high‑power lasers
• Binder Jetting: uses a liquid binder to fuse powder materials

⭐ **Advantages:**
• Design freedom: complex geometries, internal channels, lattice structures
• Customization: ideal for personalized products (medical implants, dental devices)
• Rapid prototyping: fast design-to-part cycles
• Material efficiency: minimal waste compared to subtractive methods

⚠️ **Limitations:**
• Slower than mass-production methods
• Material choices can be limited depending on the process
• Surface finish may require post-processing
• Equipment can be expensive for industrial-grade systems

📌 **Where It's Used:**
• Aerospace (lightweight metal parts)
• Automotive (prototypes, custom components)
• Healthcare (prosthetics, implants)
• Consumer goods (custom products, small-batch manufacturing)
• Architecture and construction (3D-printed buildings)

Additive manufacturing represents a significant shift in how organizations approach production, from mass-manufacturing to custom, efficient, and rapidly-prototyped solutions!`;
  }
  
  // Default response - general guidance based on agent specialty
  return `I understand you're asking about "${query}". As a Strategic Leader, let me provide some guidance...

I don't have specific information about this topic in my current knowledge base. However, I recommend trying one of the following:

🤝 Ask another SME or expert - They may have specialized knowledge in this area
🔄 Rephrase your question - Try using different keywords
📚 Browse our knowledge base topics - I can help with AI Literacy, Frameworks, Governance, KPIs, Compliance, and more

Would you like to explore a different topic or connect with another expert?`;
}